import React, { useState } from 'react';
import { useAnalyticsStore, usePlatformsStore } from '../stores';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Eye,
  Heart,
  MousePointerClick,
  Users,
  RefreshCw,
  Download,
  Calendar,
  Twitter,
  Instagram,
  Linkedin,
  Facebook,
  Youtube,
  Music,
  Clock,
} from 'lucide-react';

const platformIcons = {
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  facebook: Facebook,
  youtube: Youtube,
  tiktok: Music,
};

const COLORS = ['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

function StatCard({ title, value, change, changeType, icon: Icon, subtext }) {
  const isPositive = changeType === 'positive';
  
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtext && (
            <p className="text-xs text-gray-400 mt-1">{subtext}</p>
          )}
        </div>
        <div className={`p-2.5 rounded-xl ${
          isPositive ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'
        }`}>
          <Icon className={`w-5 h-5 ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`} />
        </div>
      </div>
      {change !== undefined && (
        <div className="mt-3 flex items-center gap-1.5">
          {isPositive ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          <span className={`text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {change}%
          </span>
          <span className="text-sm text-gray-500">vs last period</span>
        </div>
      )}
    </div>
  );
}

export default function Analytics() {
  const [timeRange, setTimeRange] = useState('7d');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { overview, weeklyData, platformStats, bestTimes, refreshAnalytics } = useAnalyticsStore();
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshAnalytics();
    setIsRefreshing(false);
  };
  
  const stats = [
    { 
      title: 'Total Impressions', 
      value: overview.totalImpressions, 
      change: 12.5, 
      changeType: 'positive', 
      icon: Eye,
      subtext: 'People who saw your content'
    },
    { 
      title: 'Engagements', 
      value: overview.totalEngagements, 
      change: 8.2, 
      changeType: 'positive', 
      icon: Heart,
      subtext: 'Likes, comments, shares'
    },
    { 
      title: 'Link Clicks', 
      value: overview.totalClicks, 
      change: -2.4, 
      changeType: 'negative', 
      icon: MousePointerClick,
      subtext: 'Clicks to your links'
    },
    { 
      title: 'Engagement Rate', 
      value: `${overview.engagementRate}%`, 
      change: 15.3, 
      changeType: 'positive', 
      icon: TrendingUp,
      subtext: 'Average across platforms'
    },
  ];
  
  const pieData = platformStats.map(p => ({
    name: p.platform,
    value: p.impressions,
  }));
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Track your social media performance
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Time range selector */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="input py-2 w-auto"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="12m">Last 12 months</option>
          </select>
          
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="btn-secondary"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-1.5" />
            Export
          </button>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <StatCard key={i} {...stat} />
        ))}
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance over time */}
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Performance Over Time</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} 
                />
                <Line 
                  type="monotone" 
                  dataKey="impressions" 
                  stroke="#0ea5e9" 
                  strokeWidth={2}
                  dot={{ fill: '#0ea5e9', strokeWidth: 0, r: 4 }}
                  name="Impressions"
                />
                <Line 
                  type="monotone" 
                  dataKey="engagements" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  dot={{ fill: '#8b5cf6', strokeWidth: 0, r: 4 }}
                  name="Engagements"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Platform breakdown */}
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Impressions by Platform</h3>
          <div className="h-72 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => value.toLocaleString()}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            {pieData.map((entry, i) => (
              <div key={entry.name} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: COLORS[i % COLORS.length] }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                  {entry.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Platform Details */}
      <div className="card">
        <div className="p-5 border-b border-gray-200 dark:border-gray-800">
          <h3 className="font-semibold text-gray-900 dark:text-white">Platform Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800">
                <th className="text-left py-4 px-5 text-sm font-medium text-gray-500">Platform</th>
                <th className="text-right py-4 px-5 text-sm font-medium text-gray-500">Followers</th>
                <th className="text-right py-4 px-5 text-sm font-medium text-gray-500">Impressions</th>
                <th className="text-right py-4 px-5 text-sm font-medium text-gray-500">Engagements</th>
                <th className="text-right py-4 px-5 text-sm font-medium text-gray-500">Eng. Rate</th>
                <th className="text-right py-4 px-5 text-sm font-medium text-gray-500">Posts</th>
              </tr>
            </thead>
            <tbody>
              {platformStats.map((stat, i) => {
                const Icon = platformIcons[stat.platform] || Users;
                const engRate = ((stat.engagements / stat.impressions) * 100).toFixed(1);
                
                return (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                          <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white capitalize">
                          {stat.platform}
                        </span>
                      </div>
                    </td>
                    <td className="text-right py-4 px-5 text-gray-600 dark:text-gray-400">
                      {stat.followers.toLocaleString()}
                    </td>
                    <td className="text-right py-4 px-5 text-gray-600 dark:text-gray-400">
                      {stat.impressions.toLocaleString()}
                    </td>
                    <td className="text-right py-4 px-5 text-gray-600 dark:text-gray-400">
                      {stat.engagements.toLocaleString()}
                    </td>
                    <td className="text-right py-4 px-5">
                      <span className={`font-medium ${
                        parseFloat(engRate) > 5 
                          ? 'text-green-600' 
                          : parseFloat(engRate) > 2 
                            ? 'text-yellow-600' 
                            : 'text-gray-600'
                      }`}>
                        {engRate}%
                      </span>
                    </td>
                    <td className="text-right py-4 px-5 text-gray-600 dark:text-gray-400">
                      {stat.posts}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Best Times to Post */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-brand-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Best Times to Post</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Based on your audience's activity patterns
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {bestTimes.map((day, i) => (
            <div key={i} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
              <p className="font-medium text-gray-900 dark:text-white mb-2">{day.day}</p>
              <div className="space-y-1">
                {day.times.map((time, j) => (
                  <div 
                    key={j} 
                    className={`text-sm px-2 py-1 rounded ${
                      j === 0 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                        : 'text-gray-500'
                    }`}
                  >
                    {time}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
